import React, { useState, useEffect } from "react";
import Resources from "../../types/Resources";
import Department from "../../types/Department";
import { deleteDepartment, getDepartments, postDepartment, updateDepartment } from "../../apiClient";
import Result from "../../types/Result";
import LoadingSpinner from "../Common/LoadingSpinner";
import { showDangerDialog, showSuccessAlert } from "../../common";
import { Modal } from "react-bootstrap";
import DepartmentCreatedResponse from "../../types/responses/DepartmentCreatedResponse";
import DepartmentFilter, { defaultDepartmentFilter } from "../../types/filters/DepartmentFilter";
import { applyDepartmentFilter } from "../../common/departmentUtils";
import ProblemDetails, { problemDetailsHasErrors } from "../../types/responses/ProblemDetails";

type DepartmentsProps = {
    resources: Resources;
    userCanCreateDepartments: boolean;
};

function Departments({ resources, userCanCreateDepartments }: DepartmentsProps) {
    const [departments, setDepartments] = useState<Department[]>([]);
    const [modalDepartmentId, setModalDepartmentId] = useState(0);
    const [showDepartmentModal, setShowDepartmentModal] = useState(false);
    const [departmentFilter, setDepartmentFilter] = useState(defaultDepartmentFilter());
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        async function fetchDataAsync() {
            const getDepartmentsResult: Result<Department[], ProblemDetails> = await getDepartments();

            if (getDepartmentsResult.failed) {
                console.error(getDepartmentsResult.error);
                showDangerDialog(resources.get("SomethingWentWrong"), resources.get("PageLoadError"));
                setIsLoading(false);
                return;
            }

            setDepartments(getDepartmentsResult.value);
            setIsLoading(false);
        }

        fetchDataAsync();
    }, []);

    if (isLoading) {
        return <LoadingSpinner />;
    }

    function hideDepartmentModalHandler() {
        setShowDepartmentModal(false);
    }

    function createDepartmentButtonClickHandler() {
        setModalDepartmentId(0);
        setShowDepartmentModal(true);
    }

    function tableRowClickHandler(clickedDepartment: Department) {
        setModalDepartmentId(clickedDepartment.id);
        setShowDepartmentModal(true);
    }

    async function createDepartmentHandler(
        newDepartment: Department
    ): Promise<Result<DepartmentCreatedResponse, ProblemDetails>> {
        const postResult = await postDepartment(newDepartment);

        if (postResult.failed) {
            if (postResult.error) {
                console.error(postResult.error);
                showDangerDialog(resources.get("SomethingWentWrong"), resources.get("PostDepartmentError"));
                hideDepartmentModalHandler();
            }

            return postResult;
        }

        const createdDepartmentId = postResult.value.createdDepartmentId;

        setDepartments((prevValue) =>
            [...prevValue, { ...newDepartment, id: createdDepartmentId }].sort((a, b) => a.name.localeCompare(b.name))
        );
        setShowDepartmentModal(false);
        showSuccessAlert(resources.get("DepartmentCreated"));

        return postResult;
    }

    async function updateDepartmentHandler(updatedDepartment: Department): Promise<Result<void, ProblemDetails>> {
        const updateResult = await updateDepartment(updatedDepartment.id, updatedDepartment);

        if (updateResult.failed) {
            if (!problemDetailsHasErrors(updateResult.error)) {
                showDangerDialog(resources.get("SomethingWentWrong"), resources.get("UpdateDepartmentError"));
                console.error(updateResult.error);
                hideDepartmentModalHandler();
            }

            return updateResult;
        }

        setDepartments((prevValue) =>
            prevValue
                .map((d) => (d.id === updatedDepartment.id ? updatedDepartment : d))
                .sort((a, b) => a.name.localeCompare(b.name))
        );

        setShowDepartmentModal(false);
        showSuccessAlert(resources.get("DepartmentUpdated"));

        return updateResult;
    }

    async function deleteDepartmentHandler(departmentToDelete: Department): Promise<Result<void, ProblemDetails>> {
        setDepartments((prevValue) => prevValue.filter((d) => d.id !== departmentToDelete.id));
        setShowDepartmentModal(false);

        const deleteResult = await deleteDepartment(departmentToDelete.id);

        if (deleteResult.failed) {
            console.error(deleteResult.error);
            setDepartments((prevValue) =>
                [...prevValue, departmentToDelete].sort((a, b) => a.name.localeCompare(b.name))
            );
            showDangerDialog(resources.get("SomethingWentWrong"), resources.get("DeleteDepartmentError"));

            return deleteResult;
        }

        showSuccessAlert(resources.get("DepartmentDeleted"));

        return deleteResult;
    }

    function departmentFilterChangeHandler(updatedProperty: keyof DepartmentFilter, updatedValue: any) {
        setDepartmentFilter((prevValue) => ({ ...prevValue, [updatedProperty]: updatedValue }));
    }

    const filteredDepartments = applyDepartmentFilter(departments, departmentFilter);

    const rows = filteredDepartments.map((d, i) => (
        <DepartmentTableRow
            department={d}
            isLastRow={i === departments.length - 1}
            onClick={() => tableRowClickHandler(d)}
            key={d.id}
        />
    ));

    const modalDepartment = departments.find((d) => d.id === modalDepartmentId);

    return (
        <div className="m-3">
            <div className="d-flex justify-content-end mb-1">
                <div>
                    <input
                        type="text"
                        className="form-control"
                        value={departmentFilter.searchText}
                        onChange={(e) => departmentFilterChangeHandler("searchText", e.target.value)}
                        style={{ width: 512 }}
                        placeholder={resources.get("Search")}
                    />
                </div>
                {userCanCreateDepartments ? (
                    <button className="btn btn-primary ms-3 ps-5 pe-5" onClick={createDepartmentButtonClickHandler}>
                        <i className="fas fa-plus me-1"></i>
                        {resources.get("NewDepartment")}
                    </button>
                ) : null}
            </div>
            <div className="card">
                <table className="table table-hover-effect mb-0">
                    <thead>
                        <tr>
                            <td className="fw-bold">{resources.get("Name")}</td>
                            <td className="fw-bold">{resources.get("Abbreviation")}</td>
                        </tr>
                    </thead>
                    <tbody>{rows}</tbody>
                </table>
            </div>
            <DepartmentModal
                department={modalDepartment}
                resources={resources}
                onHide={hideDepartmentModalHandler}
                show={showDepartmentModal}
                userCanCreateDepartments={userCanCreateDepartments}
                onCreateDepartment={createDepartmentHandler}
                onUpdateDepartment={updateDepartmentHandler}
                onDeleteDepartment={deleteDepartmentHandler}
            />
        </div>
    );
}

type DepartmentListItemProps = {
    department: Department;
    isLastRow: boolean;
    onClick: () => void;
};

function DepartmentTableRow({ department, isLastRow, onClick }: DepartmentListItemProps) {
    const borderBottomClass = isLastRow ? "border-bottom-0" : "";

    return (
        <tr className={borderBottomClass} onClick={onClick}>
            <td className={borderBottomClass}>{department.name}</td>
            <td className={borderBottomClass}>{department.abbreviation}</td>
        </tr>
    );
}

type DepartmentModalProps = {
    department: Department | undefined;
    resources: Resources;
    show: boolean;
    onHide: () => void;
    userCanCreateDepartments: boolean;
    onCreateDepartment: (newDepartment: Department) => Promise<Result<DepartmentCreatedResponse, ProblemDetails>>;
    onUpdateDepartment: (updatedDepartment: Department) => Promise<Result<void, ProblemDetails>>;
    onDeleteDepartment: (departmentToDelete: Department) => Promise<Result<void, ProblemDetails>>;
};

function DepartmentModal({
    department,
    resources,
    show,
    onHide,
    userCanCreateDepartments,
    onCreateDepartment,
    onUpdateDepartment,
    onDeleteDepartment
}: DepartmentModalProps) {
    const [nameInput, setNameInput] = useState("");
    const [nameError, setNameError] = useState("");
    const [abbreviationInput, setAbbreviationInput] = useState("");
    const [abbreviationError, setAbbreviationError] = useState("");
    const [requestInProgress, setRequestInProgress] = useState(false);

    useEffect(() => {
        if (department) {
            setNameInput(department.name);
            setAbbreviationInput(department.abbreviation);
            setAbbreviationError("");
            setNameError("");

            return;
        }

        setNameInput("");
        setAbbreviationInput("");
    }, [department]);

    function nameChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
        setNameInput(event.target.value);
    }

    function abbreviationChangeHandler(event: React.ChangeEvent<HTMLInputElement>) {
        setAbbreviationInput(event.target.value.toUpperCase());
    }

    async function createClickHandler() {
        if (!nameInput || abbreviationInput.length !== 2 || requestInProgress) {
            return;
        }

        const department: Department = {
            id: 0,
            abbreviation: abbreviationInput,
            name: nameInput,
            canDelete: true,
            canUpdate: true,
            canAcceptPermits: false,
            canCreatePermits: false
        };

        setRequestInProgress(true);
        const createResult = await onCreateDepartment(department);
        setRequestInProgress(false);

        setNameError("");
        setAbbreviationError("");

        if (createResult.failed) {
            handleFailedResult(createResult);
            return;
        }

        setNameInput("");
        setAbbreviationInput("");
    }

    async function updateClickHandler() {
        if (!department || !nameInput || requestInProgress) {
            return;
        }

        const updatedDepartment: Department = { ...department, name: nameInput };

        setRequestInProgress(true);
        const updateResult = await onUpdateDepartment(updatedDepartment);
        setRequestInProgress(false);

        setNameError("");

        if (updateResult.failed) {
            handleFailedResult(updateResult);
            return;
        }
    }

    async function deleteClickHandler() {
        if (!department || requestInProgress) {
            return;
        }

        setRequestInProgress(true);
        await onDeleteDepartment(department);
        setRequestInProgress(false);
    }

    function handleFailedResult(
        failedResult: Result<void, ProblemDetails> | Result<DepartmentCreatedResponse, ProblemDetails>
    ) {
        const nameErrors = failedResult.error.errors.Name;
        if (nameErrors && nameErrors.length > 0) {
            setNameError(nameErrors[0]);
        }

        const abbreviationErrors = failedResult.error.errors.Abbreviation;
        if (abbreviationErrors && abbreviationErrors.length > 0) {
            setAbbreviationError(abbreviationErrors[0]);
        }
    }

    const showCreateDepartmentButton = !department && userCanCreateDepartments;
    const showUpdateDepartmentButton = !!department && department.canUpdate;
    const showRemoveDepartmentButton = !!department && department.canDelete;

    const disableNameInput = !!department && !department.canUpdate;
    const disableAbbreviationInput = !!department;

    const disableCreateButton = !nameInput || abbreviationInput.length !== 2 || requestInProgress;
    const disableUpdateButton = !nameInput || requestInProgress;

    const showFooter = showCreateDepartmentButton || showUpdateDepartmentButton || showRemoveDepartmentButton;

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton>
                <h1 className="h4">{department ? department.name : resources.get("NewDepartment")}</h1>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-2">
                    <label className="form-label mb-0" htmlFor="department-name">
                        {resources.get("Name")}
                    </label>
                    <input
                        id="department-name"
                        className="form-control"
                        type="text"
                        value={nameInput}
                        onChange={nameChangeHandler}
                        readOnly={disableNameInput}
                    />
                    <span className="text-danger">{nameError}</span>
                </div>
                <div>
                    <label className="form-label mb-0" htmlFor="department-abbreviation">
                        {resources.get("Abbreviation2Characters")}
                    </label>
                    <input
                        id="department-abbreviation"
                        className="form-control"
                        type="text"
                        value={abbreviationInput}
                        onChange={abbreviationChangeHandler}
                        readOnly={disableAbbreviationInput}
                    />
                    <span className="text-danger">{abbreviationError}</span>
                </div>
            </Modal.Body>
            {showFooter ? (
                <Modal.Footer>
                    {showCreateDepartmentButton ? (
                        <button className="btn btn-primary" disabled={disableCreateButton} onClick={createClickHandler}>
                            <i className="fas fa-plus me-2"></i>
                            {resources.get("CreateDepartment")}
                        </button>
                    ) : null}
                    {showUpdateDepartmentButton ? (
                        <button className="btn btn-primary" disabled={disableUpdateButton} onClick={updateClickHandler}>
                            <i className="fas fa-save me-2"></i>
                            {resources.get("UpdateDepartment")}
                        </button>
                    ) : null}
                    {showRemoveDepartmentButton ? (
                        <button className="btn btn-danger" onClick={deleteClickHandler}>
                            <i className="fas fa-trash me-2"></i>
                            {resources.get("RemoveDepartment")}
                        </button>
                    ) : null}
                </Modal.Footer>
            ) : null}
        </Modal>
    );
}

export default Departments;
